import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiError, PERMISSION, can, conversationApi, formatIst, inboxApi, useAuth,
  type BotRun, type Conversation,
} from '@wa/shared';

/**
 * The kebab beside the chat header: add to a broadcast list, clear the pane,
 * block, and read what the bot did.
 *
 * Three of the four are features we already had with no way in from this screen.
 * The fourth — block — is deliberately NOT wired to opt-out, however alike they
 * look: opting out is the customer's decision and is evidence, blocking is ours
 * and is reversible. Collapsing them would let an agent forge a consent trail
 * with two clicks. See migration 0029.
 */
export function ChatMenu({ conversation, onStatus }: {
  conversation: Conversation;
  /** Pending lives here because the details panel no longer sets status — see F87. */
  onStatus: (status: number) => void;
}) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [sheet, setSheet] = useState<null | 'lists' | 'block' | 'runs' | 'clear'>(null);
  const [error, setError] = useState<string | null>(null);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const blocked = Boolean(conversation.blockedAt);
  const fail = (e: unknown) => setError(e instanceof ApiError ? e.message : 'That did not work.');

  const refresh = () => {
    setError(null);
    void queryClient.invalidateQueries({ queryKey: ['conversations'] });
    void queryClient.invalidateQueries({ queryKey: ['conversation-events', conversation.id] });
    void queryClient.invalidateQueries({ queryKey: ['messages', conversation.id] });
  };

  const block = useMutation({
    mutationFn: (reason: string | null) =>
      blocked ? conversationApi.unblock(conversation.contactId)
              : conversationApi.block(conversation.contactId, reason),
    onSuccess: () => { setSheet(null); refresh(); },
    onError: fail,
  });

  const clear = useMutation({
    mutationFn: () => conversationApi.clear(conversation.id),
    onSuccess: () => { setSheet(null); refresh(); },
    onError: fail,
  });

  // Pending is a real filter on the API and the details panel's three-way select
  // was its only way in. Matching the reference removed that select, so without
  // this line the status would exist in the database, be filterable in the query
  // string, and be unreachable from the product.
  const pending = conversation.status === 1;

  const items: Array<{ key: string; label: string; run: () => void; tone?: 'bad'; hidden?: boolean }> = [
    { key: 'pending', label: pending ? 'Take off pending' : 'Mark as pending',
      run: () => onStatus(pending ? 0 : 1), hidden: !can(user, PERMISSION.inboxSend) },
    { key: 'lists', label: 'Add to broadcast list', run: () => setSheet('lists'),
      hidden: !can(user, PERMISSION.segmentsWrite) },
    { key: 'clear', label: 'Clear chat', run: () => setSheet('clear') },
    { key: 'block', label: blocked ? 'Unblock' : 'Block', run: () => setSheet('block'),
      tone: blocked ? undefined : 'bad' as const, hidden: !can(user, PERMISSION.inboxSend) },
    { key: 'runs', label: 'View recent bot runs', run: () => setSheet('runs') },
  ].filter((item) => !item.hidden);

  return (
    <div ref={root} className="relative shrink-0">
      <button
        type="button"
        onClick={() => { setError(null); setOpen((v) => !v); }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Conversation menu"
        className="tap grid size-8 place-items-center rounded-md text-(--color-muted) hover:bg-(--color-sunken) hover:text-(--color-ink)"
      >
        <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
          <circle cx="10" cy="4.2" r="1.5" fill="currentColor" />
          <circle cx="10" cy="10" r="1.5" fill="currentColor" />
          <circle cx="10" cy="15.8" r="1.5" fill="currentColor" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-20 mt-1 w-56 overflow-hidden rounded-(--radius-panel) border border-(--color-line) bg-(--color-surface) py-1 shadow-(--shadow-pop)"
        >
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              onClick={() => { setOpen(false); item.run(); }}
              className={[
                'tap block w-full px-3 py-2 text-left text-small transition-colors hover:bg-(--color-sunken)',
                item.tone === 'bad' ? 'text-(--color-bad)' : 'text-(--color-ink)',
              ].join(' ')}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}

      {sheet === 'lists' && (
        <ListPicker conversation={conversation} onClose={() => setSheet(null)} />
      )}

      {sheet === 'runs' && (
        <BotRuns conversationId={conversation.id} onClose={() => setSheet(null)} />
      )}

      {sheet === 'clear' && (
        <Confirm
          title="Clear this chat?"
          /* Says what it does, because "clear" reads as "delete" and this is not
             that. An agent who believes they have erased something and has not is
             a worse outcome than one who reads a sentence. */
          body="The messages stay on record and the customer keeps their copy. They stop showing in this pane for everyone here."
          confirmLabel="Clear"
          busy={clear.isPending}
          error={error}
          onConfirm={() => clear.mutate()}
          onClose={() => { setError(null); setSheet(null); }}
        />
      )}

      {sheet === 'block' && (
        <BlockDialog
          blocked={blocked}
          name={conversation.displayName ?? `+${conversation.waId}`}
          busy={block.isPending}
          error={error}
          onConfirm={(reason) => block.mutate(reason)}
          onClose={() => { setError(null); setSheet(null); }}
        />
      )}
    </div>
  );
}

/* --------------------------------------------------------------- broadcast -- */

/**
 * "Add to broadcast list" is DoubleTick's phrase for putting a contact on a
 * static segment, which we have had since M5. A menu item, not a feature — and
 * only static lists are offered, because a dynamic list is defined by its rules
 * and adding one person to it by hand would be a lie about what it contains.
 */
function ListPicker({ conversation, onClose }: { conversation: Conversation; onClose: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const segments = useQuery({ queryKey: ['segments'], queryFn: inboxApi.segments });

  const add = useMutation({
    mutationFn: ({ segmentId }: { segmentId: number; name: string }) =>
      inboxApi.addSegmentMembers(segmentId, [conversation.contactId]),
    onSuccess: (_data, variables) => { setError(null); setDone(variables.name); },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'That did not save.'),
  });

  const statics = (segments.data ?? []).filter((s) => s.kind === 0);

  return (
    <Sheet title="Add to a broadcast list" onClose={onClose}>
      {segments.isLoading && <p className="text-small text-(--color-muted)">Loading…</p>}

      {!segments.isLoading && statics.length === 0 && (
        <p className="text-small text-(--color-muted)">
          There are no fixed lists yet. Rule-based lists decide their own members, so
          somebody cannot be added to one by hand — make a fixed list under Contacts.
        </p>
      )}

      {statics.length > 0 && (
        <ul className="flex flex-col gap-1">
          {statics.map((segment) => (
            <li key={segment.id}>
              <button
                type="button"
                disabled={add.isPending}
                onClick={() => add.mutate({ segmentId: segment.id, name: segment.name })}
                className="tap w-full rounded-md px-2.5 py-2 text-left text-small hover:bg-(--color-sunken) disabled:opacity-60"
              >
                {segment.name}
              </button>
            </li>
          ))}
        </ul>
      )}

      {done && <p className="mt-2 text-small text-(--color-good)">Added to {done}.</p>}
      {error && <p role="alert" className="mt-2 text-small text-(--color-bad)">{error}</p>}
    </Sheet>
  );
}

/* ---------------------------------------------------------------- bot runs -- */

const RUN_STATUS: Record<number, string> = {
  0: 'Running', 1: 'Finished', 2: 'Handed to an agent', 3: 'Abandoned', 4: 'Failed',
};

function BotRuns({ conversationId, onClose }: { conversationId: number; onClose: () => void }) {
  const runs = useQuery({
    queryKey: ['bot-runs', conversationId],
    queryFn: () => conversationApi.botRuns(conversationId),
  });

  return (
    <Sheet title="Recent bot runs" onClose={onClose}>
      {runs.isLoading && <p className="text-small text-(--color-muted)">Loading…</p>}

      {!runs.isLoading && (runs.data ?? []).length === 0 && (
        <p className="text-small text-(--color-muted)">No bot has run in this conversation.</p>
      )}

      <ul className="flex flex-col gap-2">
        {(runs.data ?? []).map((run: BotRun) => (
          <li key={run.id} className="rounded-(--radius-panel) border border-(--color-line) bg-(--color-sunken) p-2.5">
            <p className="flex items-baseline justify-between gap-2 text-small">
              <span className="min-w-0 truncate font-medium">{run.flowName ?? 'A deleted flow'}</span>
              <span className="shrink-0 text-micro text-(--color-muted)">v{run.flowVersion ?? '?'}</span>
            </p>
            <p className="mt-1 flex items-baseline justify-between gap-2 text-micro text-(--color-muted)">
              <span className="truncate">
                {RUN_STATUS[run.status] ?? 'Unknown'}
                {run.currentNode ? ` · stopped at ${run.currentNode}` : ''}
              </span>
              <span className="shrink-0 tabular-nums">{formatIst(run.startedAt)}</span>
            </p>
          </li>
        ))}
      </ul>
    </Sheet>
  );
}

/* -------------------------------------------------------------------- block -- */

function BlockDialog({ blocked, name, busy, error, onConfirm, onClose }: {
  blocked: boolean;
  name: string;
  busy: boolean;
  error: string | null;
  onConfirm: (reason: string | null) => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');

  if (blocked) {
    return (
      <Confirm
        title={`Unblock ${name}?`}
        body="Messages can be sent to them again straight away."
        confirmLabel="Unblock"
        busy={busy}
        error={error}
        onConfirm={() => onConfirm(null)}
        onClose={onClose}
      />
    );
  }

  return (
    <Sheet title={`Block ${name}?`} onClose={onClose}>
      <p className="text-small text-(--color-muted)">
        Nothing can be sent to them — replies, templates or campaigns — until somebody
        unblocks them. This is our decision and is not recorded as the customer opting
        out.
      </p>

      <label className="mt-3 block">
        <span className="mb-1 block text-micro font-medium tracking-wide text-(--color-muted) uppercase">
          Reason (optional)
        </span>
        <input
          value={reason}
          maxLength={200}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Abusive language"
          className="tap w-full rounded-md border border-(--color-line) bg-(--color-sunken) px-2.5 py-1.5 text-small outline-none focus:border-(--color-brand)"
        />
      </label>

      {error && <p role="alert" className="mt-2 text-small text-(--color-bad)">{error}</p>}

      <div className="mt-4 flex justify-end gap-2">
        <button type="button" onClick={onClose}
                className="tap rounded-md px-3 py-1.5 text-small text-(--color-muted) hover:text-(--color-ink)">
          Cancel
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => onConfirm(reason.trim() || null)}
          className="tap rounded-md bg-(--color-bad) px-3 py-1.5 text-small font-medium text-white disabled:opacity-60"
        >
          {busy ? 'Blocking…' : 'Block'}
        </button>
      </div>
    </Sheet>
  );
}

/* ----------------------------------------------------------------- plumbing -- */

function Confirm({ title, body, confirmLabel, busy, error, onConfirm, onClose }: {
  title: string;
  body: string;
  confirmLabel: string;
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Sheet title={title} onClose={onClose}>
      <p className="text-small text-(--color-muted)">{body}</p>
      {error && <p role="alert" className="mt-2 text-small text-(--color-bad)">{error}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <button type="button" onClick={onClose}
                className="tap rounded-md px-3 py-1.5 text-small text-(--color-muted) hover:text-(--color-ink)">
          Cancel
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onConfirm}
          className="tap rounded-md bg-(--color-brand) px-3 py-1.5 text-small font-medium text-(--color-brand-ink) disabled:opacity-60"
        >
          {busy ? 'Working…' : confirmLabel}
        </button>
      </div>
    </Sheet>
  );
}

/**
 * A centred dialog rather than a dropdown panel.
 *
 * Every one of these asks a question whose answer costs something — a block, a
 * cleared pane, a contact on a broadcast list — and a panel that closes when the
 * pointer wanders is the wrong shape for a question like that.
 */
function Sheet({ title, children, onClose }: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-40 grid place-items-center bg-black/35 p-4" role="presentation" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
        className="max-h-[80vh] w-full max-w-sm overflow-y-auto rounded-(--radius-panel) border border-(--color-line) bg-(--color-surface) p-4 shadow-(--shadow-pop)"
      >
        <h2 className="mb-2 text-lead font-semibold tracking-tight">{title}</h2>
        {children}
      </div>
    </div>
  );
}
